import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, fmtKg, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Cow, Trash, Drop } from "@phosphor-icons/react";
import { PageLoading } from "@/components/layout/Layout";
import { useT } from "@/contexts/I18nContext";
import { SystemBadge } from "@/components/Badges";
import { ListState } from "@/components/ListState";
import { ExportMenu } from "@/components/ExportMenu";

const EMPTY = { rfid: "", name: "", lot: "", breed: "", birth_date: "", production_type: "weight" };

export default function Animals() {
    const { t } = useT();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(EMPTY);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/animals");
            setItems(r.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.post("/animals", {
                ...form,
                birth_date: form.birth_date || null,
                diet_type: form.diet_type || null,
            });
            toast.success(t("animal_created"));
            setOpen(false);
            setForm(EMPTY);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm(t("confirm_delete_animal"))) return;
        try {
            await api.delete(`/animals/${id}`);
            toast.success(t("animal_deleted"));
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-6" data-testid="animals-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">{t("animals_title")}</h1>
                    <p className="text-sm text-muted-foreground mt-1">{t("animals_subtitle")}</p>
                </div>
                <div className="flex items-center gap-2">
                    <ExportMenu endpoint="/export/animals" baseName="animals" testIdPrefix="animals-export" />
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button className="bg-[#1A3626] hover:bg-[#132A1D] text-white" data-testid="new-animal-btn">
                                <Plus size={16} className="mr-2" /> {t("new_animal")}
                            </Button>
                        </DialogTrigger>
                    <DialogContent className="max-w-md">
                        <DialogHeader>
                            <DialogTitle className="font-display">{t("new_animal_title")}</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={submit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label>{t("rfid")}</Label>
                                    <Input required value={form.rfid} onChange={(e) => setForm({ ...form, rfid: e.target.value })} data-testid="animal-rfid-input" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>{t("name_or_id")}</Label>
                                    <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="animal-name-input" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>{t("lot")}</Label>
                                    <Input required value={form.lot} onChange={(e) => setForm({ ...form, lot: e.target.value })} data-testid="animal-lot-input" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label>{t("breed")}</Label>
                                    <Input value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} data-testid="animal-breed-input" />
                                </div>
                                <div className="space-y-1.5 col-span-2">
                                    <Label>{t("production_type_label")}</Label>
                                    <Select value={form.production_type} onValueChange={(v) => setForm({ ...form, production_type: v })}>
                                        <SelectTrigger data-testid="animal-production-type-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="weight" data-testid="production-weight-option">{t("production_weight")}</SelectItem>
                                            <SelectItem value="milk" data-testid="production-milk-option">{t("production_milk")}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5 col-span-2">
                                    <Label>{t("production_system")} *</Label>
                                    <Select value={form.production_system} onValueChange={(v) => setForm({ ...form, production_system: v })}>
                                        <SelectTrigger data-testid="animal-system-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="feedlot" data-testid="system-feedlot-option">{t("sys_feedlot")}</SelectItem>
                                            <SelectItem value="semi" data-testid="system-semi-option">{t("sys_semi")}</SelectItem>
                                            <SelectItem value="pasture" data-testid="system-pasture-option">{t("sys_pasture")}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-1.5 col-span-2">
                                    <Label>{t("diet_type_optional")}</Label>
                                    <Input
                                        value={form.diet_type}
                                        onChange={(e) => setForm({ ...form, diet_type: e.target.value })}
                                        placeholder={t("diet_placeholder")}
                                        data-testid="animal-diet-input"
                                    />
                                </div>
                                <div className="space-y-1.5 col-span-2">
                                    <Label>{t("birth_date")}</Label>
                                    <Input type="date" value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} data-testid="animal-birth-input" />
                                </div>
                            </div>
                            <DialogFooter>
                                <Button type="submit" disabled={saving} className="bg-[#1A3626] hover:bg-[#132A1D] text-white" data-testid="animal-save-btn">
                                    {saving ? t("saving") : t("save")}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
                </div>
            </div>

            <Card className="card-paper shadow-none overflow-hidden">
                <ListState
                    loading={loading}
                    isEmpty={items.length === 0}
                    emptyIcon={Cow}
                    emptyTitle={t("no_animals")}
                    emptyHint={t("register_first_animal")}
                >
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t("rfid")}</TableHead>
                                <TableHead>{t("name")}</TableHead>
                                <TableHead>{t("lot")}</TableHead>
                                <TableHead>{t("production_type_label")}</TableHead>
                                <TableHead>{t("production_system")}</TableHead>
                                <TableHead>{t("breed")}</TableHead>
                                <TableHead className="text-right">{t("last_weight")}</TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {items.map((a) => (
                                <TableRow key={a.id} className="hover:bg-[#E4EAE1]/40" data-testid={`animal-row-${a.rfid}`}>
                                    <TableCell className="font-medium">{a.rfid}</TableCell>
                                    <TableCell>
                                        <Link to={`/animales/${a.id}`} className="font-medium text-[#1A3626] hover:underline">{a.name}</Link>
                                    </TableCell>
                                    <TableCell><Badge variant="secondary">{a.lot}</Badge></TableCell>
                                    <TableCell>
                                        {a.production_type === "milk" ? (
                                            <Badge className="bg-[#D99533]/15 text-[#D99533] border border-[#D99533]/30 hover:bg-[#D99533]/15">
                                                <Drop size={12} className="mr-1" weight="fill" /> {t("production_milk")}
                                            </Badge>
                                        ) : (
                                            <Badge className="bg-[#1A3626]/10 text-[#1A3626] border border-[#1A3626]/20 hover:bg-[#1A3626]/10">
                                                {t("production_weight")}
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell><SystemBadge system={a.production_system || "feedlot"} /></TableCell>
                                    <TableCell className="text-muted-foreground">{a.breed || "—"}</TableCell>
                                    <TableCell className="text-right metric-number">{a.production_type === "milk" ? "—" : fmtKg(a.current_weight)}</TableCell>
                                    <TableCell className="text-right">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-[#C55D42] hover:text-[#C55D42] hover:bg-[#C55D42]/10"
                                            onClick={() => remove(a.id)}
                                            data-testid={`delete-animal-${a.rfid}`}
                                        >
                                            <Trash size={16} />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </ListState>
            </Card>
        </div>
    );
}
